import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import _ from 'underscore';

import {OrganizationDefaultSchemaCollaboratorsConfiguration} from 'auth/__generated/OrganizationDefaultSchemaCollaboratorsConfiguration';
import {SchemaCollaborator} from 'auth/PermissionConstants';
import {SchemaPolicy} from 'auth/PolicyConstants';
import {
  getDefaultCollaborators,
  useSchemaCollaboratorsSectionWithDefaults,
} from 'collaborator-components/useSchemaCollaboratorsSection';
import {DefaultSchemaCollaboratorsConfigurationSchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {SchemaWithOrgPermissionsSettings as CreateSchemaWithOrgPermissionsSettings} from 'schemas/SchemaWithOrgPermissionsSettings';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import StepNav, {StepItemStatus} from 'ui-library/StepNav';
import {Tab, TabContainer} from 'ui-library/TabContainer';

enum SchemaCreateKey {
  METADATA = 'METADATA',
  ACCESS_POLICIES = 'ACCESS_POLICIES',
}

interface HeaderNavigationProps {
  steps: Array<{key: SchemaCreateKey; title: string}>;
  currentStepKey: SchemaCreateKey;
  setCurrentStep: (key: SchemaCreateKey) => void;
  children: JSX.Element;
}

function HeaderNavigation(props: HeaderNavigationProps): JSX.Element {
  if (props.steps.length === 1) {
    return (
      <TabContainer
        activeTab={props.currentStepKey}
        className="u-flex-grow"
        onRequestTabChange={props.setCurrentStep}
      >
        <Tab tabKey={props.steps[0].key} title={props.steps[0].title}>
          {props.children}
        </Tab>
      </TabContainer>
    );
  }
  return (
    <div>
      <StepNav<SchemaCreateKey>
        steps={props.steps}
        statusByStep={{
          [SchemaCreateKey.METADATA]: StepItemStatus.UNVISITED,
          [SchemaCreateKey.ACCESS_POLICIES]: StepItemStatus.UNVISITED,
        }}
        currentStepKey={props.currentStepKey}
        onUpdateCurrentStep={props.setCurrentStep}
      />
      {props.children}
    </div>
  );
}

export interface SchemaWithOrgPermissionsCreateFlowProps {
  createSchema: (collaborators: Array<SchemaCollaborator>) => void;
  defaultSchemaCollaboratorsSchemaType: DefaultSchemaCollaboratorsConfigurationSchemaType;
  metadataTitle: string;
  isSchemaPermissionsEnabled: boolean;
  ownerId: string | null;
  organizations: Array<OrganizationDefaultSchemaCollaboratorsConfiguration>;
  renderMetadataSettings: (handleNext: () => void) => JSX.Element;
  policyPredicate?: (policy: SchemaPolicy) => boolean;
  schemaCollaboratorsDescription?: JSX.Element;
}

export default function SchemaWithOrgPermissionsCreateFlow(
  props: SchemaWithOrgPermissionsCreateFlowProps
): JSX.Element {
  const [currentStepKey, setCurrentStepKey] = React.useState(SchemaCreateKey.METADATA);
  const ownerOrganization = props.organizations.find((o) => o.id === props.ownerId) ?? props.organizations[0];
  const [{collaborators, minimalCollaborators}, handleUpdate] = useSchemaCollaboratorsSectionWithDefaults(
    ownerOrganization,
    props.defaultSchemaCollaboratorsSchemaType
  );
  React.useEffect(() => {
    handleUpdate(getDefaultCollaborators(ownerOrganization, props.defaultSchemaCollaboratorsSchemaType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.ownerId]);
  const steps = _.compact([
    {
      key: SchemaCreateKey.METADATA,
      title: props.metadataTitle,
    },
    props.isSchemaPermissionsEnabled && {
      key: SchemaCreateKey.ACCESS_POLICIES,
      title: 'Access Policies',
    },
  ]);
  const currentStepIndex = steps.findIndex((step) => step.key === currentStepKey);
  return (
    <div className="u-flex-grow">
      <div className="u-flex-grow">
        <HeaderNavigation steps={steps} currentStepKey={currentStepKey} setCurrentStep={setCurrentStepKey}>
          <>
            {currentStepKey === SchemaCreateKey.METADATA &&
              props.renderMetadataSettings(() => {
                setCurrentStepKey(steps[currentStepIndex + 1].key);
              })}
            {currentStepKey === SchemaCreateKey.ACCESS_POLICIES && (
              <CreateSchemaWithOrgPermissionsSettings
                isReadOnly={false}
                collaborators={collaborators}
                description={props.schemaCollaboratorsDescription}
                onUpdate={handleUpdate}
                policyPredicate={props.policyPredicate}
              />
            )}
          </>
        </HeaderNavigation>
      </div>
      {props.isSchemaPermissionsEnabled && (
        <div className={css(styles.buttonContainer)}>
          {currentStepIndex !== 0 && (
            <Button
              buttonStyle={ButtonStyle.LINK}
              text="Back"
              className="u-small-spacing-left"
              onClick={() => {
                setCurrentStepKey(steps[currentStepIndex - 1].key);
              }}
            />
          )}
          {currentStepIndex === steps.length - 1 && (
            <Button
              buttonStyle={ButtonStyle.SECONDARY}
              size={ButtonSize.MEDIUM}
              text="Create"
              className="u-small-spacing-left"
              onClick={() => {
                props.createSchema(minimalCollaborators);
              }}
              dataTestAttrs={{component: 'SchemaWithOrgPermissionsCreateFlow', element: 'createButton'}}
            />
          )}
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  buttonContainer: {
    float: 'right',
  },
});
